import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fromHex } from "@redchain/input-log";
import { api, isApiError } from "../api/client";
import { KeymapEditor } from "../components/KeymapEditor";
import { RegisterStep } from "../components/RegisterStep";
import { RomPicker } from "../components/RomPicker";
import { TouchControls } from "../components/TouchControls";
import { takePendingLobby } from "../link/activeLobby";
import { LinkStatusBar } from "../link/LinkStatusBar";
import type { RomRecord } from "../main/db";
import { computeCoreBuildHash, EmulatorHost } from "../main/EmulatorHost";
import { LinkController, type LinkResult, type LinkView } from "../main/LinkController";
import { PALETTE_LABELS, type PaletteName } from "../main/Renderer";
import { storedRom, versionLabel } from "../main/RomLoader";
import { sessionRom } from "../main/romPatch";
import { SessionClient, uuidToBytes } from "../main/SessionClient";
import { Overlay } from "../overlay/Overlay";
import { initialOverlayState, overlayReducer } from "../overlay/state";
import { SyncPendingReporter } from "../overlay/useWalletAction";
import { usePlayer } from "../state/PlayerContext";
import { autosaveText, purseText, t } from "../strings";
import { WalletMenu } from "../wallet-menu/WalletMenu";
import { useWallet } from "../wallet/WalletProvider";
import { LobbySetup } from "./LinkLobby";

type Step =
  | { kind: "loading" }
  | { kind: "register" }
  | { kind: "rom" }
  | { kind: "draining"; remaining: number }
  | { kind: "starting" }
  | { kind: "playing"; offline: boolean }
  | { kind: "superseded" }
  | { kind: "error"; message: string };

/**
 * /play — boot flow WEB-12: login → register if needed → ROM → drain pending uploads →
 * session start → worker init with the composed SRAM and the server nonce in the PKIL header.
 */
export function Play() {
  const { wallet, auth, ready } = useWallet();
  const { refresh, setInSession, lastError } = usePlayer();
  const [step, setStep] = useState<Step>({ kind: "loading" });
  const [rom, setRom] = useState<RomRecord | null>(null);
  const [lockedVersion, setLockedVersion] = useState<RomRecord["version"] | null>(null);
  const [overlay, dispatch] = useReducer(overlayReducer, initialOverlayState);
  const [showKeys, setShowKeys] = useState(false);
  const [palette, setPalette] = useState<PaletteName>("native");
  const [touch, setTouch] = useState(() => window.matchMedia?.("(pointer: coarse)").matches ?? false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<EmulatorHost | null>(null);
  const sessionRef = useRef<SessionClient | null>(null);
  const linkRef = useRef<LinkController | null>(null);
  const [link, setLink] = useState<{ view: LinkView; result: LinkResult | null }>({ view: { kind: "idle" }, result: null });
  const [showLobby, setShowLobby] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const booting = useRef(false);

  // 1–3: player record, registration, ROM (with the generation's version lock).
  const prepare = useCallback(async () => {
    const me = await refresh();
    if (!me) {
      // Offline: boot from the last canonical SRAM on this device (§7.5) if we have a ROM.
      const local = navigator.onLine ? null : await storedRom(null);
      if (local) return setRom(local);
      const detail = lastError()?.trim();
      const rateLimited = detail && /rate.?limit|too many|rpc_rate|limit \d+ per/i.test(detail);
      return setStep({
        kind: "error",
        message: rateLimited
          ? `${detail}. Wait about a minute, then reload — don't spam Reload or you'll hit the session limit again.`
          : detail
            ? `Couldn't load your player account: ${detail}`
            : "Couldn't load your player account. Check your connection and reload.",
      });
    }
    if (!me.registered) return setStep({ kind: "register" });
    setLockedVersion(me.gameVersion);
    dispatch({ type: "init", lastSyncedAt: me.lastSyncedAt ? Date.parse(me.lastSyncedAt) : null, offline: false, vault: me.vault });
    const stored = await storedRom(me.gameVersion);
    if (stored) setRom(stored);
    else setStep({ kind: "rom" });
  }, [refresh, lastError]);

  useEffect(() => {
    if (!auth || step.kind !== "loading") return;
    void prepare();
  }, [auth, step.kind, prepare]);

  // 4–6: drain, session start, worker boot.
  useEffect(() => {
    if (!rom || !wallet || booting.current) return;
    booting.current = true;
    const sc = new SessionClient(api, wallet.address);
    sessionRef.current = sc;
    let cancelled = false;

    (async () => {
      sc.onOfflineResult = (result) => {
        dispatch({ type: "offline", result });
        if (result.kind === "adopted" && result.live && !cancelled) setStep((s) => (s.kind === "playing" ? { kind: "playing", offline: false } : s));
      };
      setStep({ kind: "draining", remaining: 0 });
      const drained = await sc.drainPending((remaining) => setStep({ kind: "draining", remaining }));
      if (cancelled) return;

      setStep({ kind: "starting" });
      const coreBuildHash = await computeCoreBuildHash();
      const started = await sc.start({ romSha1: rom.sha1, coreBuildHash });
      if (cancelled) return sc.stop();
      if (started.gameVersion && started.gameVersion !== rom.version) {
        sc.stop();
        setRom(null);
        setLockedVersion(started.gameVersion);
        setStep({ kind: "rom" });
        booting.current = false;
        return;
      }

      sc.onQueue = (q) => dispatch({ type: "queue", pendingCheckpoints: q.pendingCheckpoints, online: q.online });
      sc.onCheckpointUploaded = () => dispatch({ type: "checkpointUploaded" });
      // Purse credits for this session go into the running game; ones that arrive before the worker is up wait for it.
      const earlyCredits: { creditId: string; amount: number }[] = [];
      sc.onEvent = (event) => {
        dispatch({ type: "sse", event });
        if (event.type === "purse.credit" && event.sessionId === started.sessionId) {
          if (hostRef.current) hostRef.current.purseCredit(event.creditId, event.amount);
          else earlyCredits.push(event);
        }
        // vault.changed is counted before the save's mints land; the API's count after the commit is current.
        // Mints are paid in ₽, so a deposit or refund changes what they still need.
        if (event.type === "chain.committed" || event.type === "rcd.deposited" || event.type === "rcd.refunded")
          void refresh().then((me) => {
            if (me?.vault) dispatch({ type: "vault", vault: me.vault });
          });
      };
      sc.onSuperseded = () => {
        hostRef.current?.superseded();
        dispatch({ type: "superseded" });
        setStep({ kind: "superseded" });
      };
      dispatch({ type: "notices", notices: started.notices });
      if (started.offline || drained === "offline") dispatch({ type: "init", lastSyncedAt: overlay.lastSyncedAt, offline: true });

      const canvas = canvasRef.current;
      const screen = screenRef.current;
      if (!canvas || !screen) throw new Error("screen not mounted");
      const host = new EmulatorHost(canvas, screen, sc, {
        onAutosave: (m) => dispatch({ type: "autosave", text: autosaveText(m), now: Date.now() }),
        onPurseApplied: (m) => dispatch({ type: "autosave", text: purseText(m), now: Date.now() }),
        onWalletOpen: () => {
          host.openWalletUi();
          setWalletOpen(true);
        },
        onError: (message) => setStep({ kind: "error", message: `The emulator stopped: ${message}` }),
      });
      hostRef.current = host;
      setPalette(host.renderer.getPalette());
      await host.start({
        rom: sessionRom(rom.bytes, rom.version, started.romPatch),
        sram: started.sram,
        sessionHeader: {
          sessionId: uuidToBytes(started.sessionId),
          coreBuildHash: fromHex(coreBuildHash),
          romSha1: fromHex(rom.sha1),
          startSramSha256: started.sramSha256,
          serverNonce: started.serverNonce,
        },
        autosaveTriggers: started.autosaveTriggers,
      });
      for (const c of earlyCredits.splice(0)) host.purseCredit(c.creditId, c.amount);
      const lc = new LinkController(host, sc, api, rom.version, (view, result) => setLink({ view, result }));
      linkRef.current = lc;
      // A lobby created or joined on /link waits for the Cable Club save in this session.
      const pendingLobby = takePendingLobby();
      if (pendingLobby) lc.arm(pendingLobby);
      setInSession(true);
      setStep({ kind: "playing", offline: started.offline });
    })().catch((e: unknown) => {
      // A failed boot (session start, offline fallback or worker) must not leave uploads, SSE or heartbeats running.
      sc.stop();
      if (cancelled) return;
      booting.current = false;
      let message = isApiError(e) ? (e.body.message ?? e.body.reason) : e instanceof Error ? e.message : String(e);
      // Pino's err serializer used to mask real API failures as this TypeError in the HTTP body.
      if (/circular-ref-tag|object is not extensible/i.test(message) || message === "internal" || message === "500 internal") {
        message = "Couldn't start the session. Reload and try again; if it keeps failing, your last save may still be processing.";
      }
      if (isApiError(e, "rate_limited") || isApiError(e, "rpc_rate_limited") || /limit \d+ per|too many requests|rpc_rate/i.test(message)) {
        message = `${message}. Wait about a minute before reloading — rapid retries make it worse.`;
      }
      setStep({ kind: "error", message });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rom, wallet]);

  // Teardown on leaving /play.
  useEffect(
    () => () => {
      setInSession(false);
      linkRef.current?.cancel();
      linkRef.current?.dispose();
      linkRef.current = null;
      hostRef.current?.dispose();
      hostRef.current = null;
      void sessionRef.current?.end();
      sessionRef.current = null;
    },
    [setInSession],
  );

  if (!ready) return <main className="page">Loading…</main>;
  if (!auth || !wallet)
    return (
      <main className="page">
        <p>
          <Link to="/">Log in</Link> to play.
        </p>
      </main>
    );

  const powerCycle = () => {
    if (window.confirm("Power cycle the Game Boy? Anything since your last save is lost, as on real hardware.")) hostRef.current?.powerCycle();
  };
  const linkBusy = link.view.kind !== "idle" && link.view.kind !== "ended" && link.view.kind !== "error";
  const serverRunOver = link.view.kind === "ended" && link.view.needsReload;

  return (
    <SyncPendingReporter.Provider value={(waiting) => dispatch({ type: "syncPending", waiting })}>
      <main className="play">
        <section className="stage">
          {step.kind === "register" && <RegisterStep onDone={() => setStep({ kind: "loading" })} />}
          {step.kind === "rom" && (
            <RomPicker
              lockedVersion={lockedVersion}
              onRom={(r) => {
                setRom(r);
              }}
            />
          )}
          {step.kind === "draining" && <p className="status">Uploading your last session{step.remaining ? ` (${step.remaining} left)` : ""}…</p>}
          {step.kind === "starting" && <p className="status">Loading your game{rom ? ` (${versionLabel(rom.version)})` : ""}…</p>}
          {step.kind === "error" && (
            <div className="card">
              <p className="status error">{step.message}</p>
              <button onClick={() => window.location.reload()}>Reload</button>
            </div>
          )}
          {step.kind === "superseded" && (
            <div className="card">
              <p className="status error">{t.superseded}</p>
              <button onClick={() => window.location.reload()}>Play here instead</button>
            </div>
          )}
          <div ref={screenRef} className={`screen ${step.kind === "playing" || step.kind === "superseded" ? "" : "hidden"}`}>
            <canvas ref={canvasRef} className="lcd" aria-label="Game screen" tabIndex={0} />
            {walletOpen && (
              <WalletMenu
                onClose={() => {
                  setWalletOpen(false);
                  hostRef.current?.closeWalletUi();
                }}
              />
            )}
          </div>
          {step.kind === "playing" && (
            <div className="toolbar">
              <button onClick={powerCycle} disabled={linkBusy || serverRunOver}>
                Power cycle
              </button>
              <button onClick={() => setShowLobby((v) => !v)} disabled={linkBusy || serverRunOver || step.offline}>
                Cable Club
              </button>
              <button onClick={() => void hostRef.current?.renderer.toggleFullscreen()}>Fullscreen</button>
              <select
                aria-label="Palette"
                value={palette}
                onChange={(e) => {
                  const p = e.target.value as PaletteName;
                  setPalette(p);
                  hostRef.current?.renderer.setPalette(p);
                }}
              >
                {(Object.keys(PALETTE_LABELS) as PaletteName[]).map((p) => (
                  <option key={p} value={p}>
                    {PALETTE_LABELS[p]}
                  </option>
                ))}
              </select>
              <label className="inline">
                Volume
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  defaultValue={hostRef.current?.audio.getVolume() ?? 0.8}
                  onChange={(e) => hostRef.current?.audio.setVolume(Number(e.target.value))}
                />
              </label>
              <button onClick={() => setShowKeys((v) => !v)}>Controls</button>
              <button onClick={() => setTouch((v) => !v)}>{touch ? "Hide" : "Show"} touch pad</button>
              {step.offline && overlay.offlineSession && <span className="hint">{t.offline}</span>}
            </div>
          )}
          {step.kind === "playing" && (
            <LinkStatusBar
              view={link.view}
              result={link.result}
              onCancel={() => linkRef.current?.cancel()}
              onDismiss={() => linkRef.current?.dismiss()}
            />
          )}
          {step.kind === "playing" && showLobby && !linkBusy && (
            <div className="card">
              <LobbySetup
                enterLabel="Wait at the Cable Club"
                onEnter={(lobby) => {
                  linkRef.current?.arm(lobby);
                  setShowLobby(false);
                }}
              />
            </div>
          )}
          {step.kind === "playing" && touch && hostRef.current && <TouchControls input={hostRef.current.input} />}
          {showKeys && hostRef.current && <KeymapEditor input={hostRef.current.input} onClose={() => setShowKeys(false)} />}
        </section>
        <Overlay state={overlay} dispatch={dispatch} />
      </main>
    </SyncPendingReporter.Provider>
  );
}
